import React, { ChangeEventHandler, useCallback, useEffect, useRef, useState } from "react";
import classnames from "classnames";
import { IInputFieldConfig } from "../Map/config";
import { IGroup } from "../Contexts/SearchContext";
import "./Select.scss";
import { useAppContext } from "../Contexts/AppContext";

const maxOptionLength = 30;

export const Select = ({
    title,
    config,
    options,
    defaultValue,
    onChange
}: {
    title: string;
    config: IInputFieldConfig;
    options: IGroup[];
    defaultValue: string;
    onChange: ChangeEventHandler<HTMLSelectElement>;
}): JSX.Element => {
    const { isMobile } = useAppContext();
    let longestOption = 0;
    !isMobile && options && options.map((o) => o.label.length > longestOption && (longestOption = o.label.length));

    return longestOption <= maxOptionLength ? (
        <HtmlSelect title={title} config={config} defaultValue={defaultValue} options={options} onChange={onChange} />
    ) : (
        <DivSelect title={title} config={config} defaultValue={defaultValue} options={options} onChange={onChange} />
    );
};

const HtmlSelect = ({
    title,
    config,
    options,
    defaultValue,
    onChange
}: {
    title: string;
    config: IInputFieldConfig;
    options: IGroup[];
    defaultValue: string;
    onChange: ChangeEventHandler<HTMLSelectElement>;
}) => (
    <>
        {config.title ? (
            <label className={classnames("input-label", { required: config.required })}>
                {config.title}
                {/* {config.info && <InfoButton text={config.info}></InfoButton>} */}
            </label>
        ) : null}
        <div className="select input-element" title={title}>
            <select className="sv-select" name={config.field} defaultValue={defaultValue} onChange={onChange}>
                {options &&
                    options.map((o) => (
                        <option key={o.value + "-option"} value={o.label}>
                            {o.label}
                        </option>
                    ))}
            </select>
        </div>
    </>
);

const DivSelect = ({
    title,
    config,
    options,
    defaultValue,
    onChange
}: {
    title: string;
    config: IInputFieldConfig;
    options: IGroup[];
    defaultValue: string;
    onChange: any;
}): JSX.Element => {
    const [expanded, setExpanded] = useState<boolean>(false);

    const optionsRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        expanded && optionsRef?.current?.focus();
    }, [expanded]);

    const onClick = useCallback(() => setExpanded(!expanded), [expanded]);

    const onBlur = useCallback(
        (e) => setTimeout(() => !e.relatedTarget && setExpanded(false), 200), // timeout, because Blur is faster than Click event
        [expanded]
    );

    const onSelect = (o) => {
        onChange({ target: { value: o.label } });
        setExpanded(false);
    };

    return (
        <>
            <div className="select div-select unselectable" onClick={onClick}>
                <div className="text-input">{defaultValue}</div>
            </div>
            {expanded ? (
                <div className="scrollable-content-container unselectable">
                    <div
                        ref={optionsRef}
                        className="single-select multi-options scrollable-content"
                        tabIndex={0}
                        onBlur={onBlur}
                    >
                        {options &&
                            options.map((o) => (
                                <div
                                    className={classnames("single-select-option", {
                                        selected: defaultValue === o.label
                                    })}
                                    key={o.label + "-option-" + o.value}
                                    onClick={() => onSelect(o)}
                                >
                                    <div>{o.label}</div>
                                </div>
                            ))}
                    </div>
                </div>
            ) : null}
        </>
    );
};
